import { Component, OnInit, Type } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ResolveEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import {
  distinctUntilChanged,
  filter,
  retry,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { IConfirmDialog } from './shared/interface/common';
import { User } from './shared/models/user.model';
import { AzureAdService } from './shared/services/azure-ad.service';
import { ConfirmDialogService } from './shared/services/confirm-dialog.service';
import { LocationService } from './shared/services/location.service';
import { NotificationEventService } from './shared/services/notification-event.service';
import { NotificationService } from './shared/services/notification.service';
import { OfflineDetectionService } from './shared/services/offlineDetection.service';
import { ApplicationInsightsService } from './shared/services/telemetry.service';
import { UserInfoService } from './shared/services/user-info.service';
import { WebSocketService } from './shared/services/web-socket.service';
import { DialogActionApp, OptionButtonType } from './shared/type';
import { AppConstant } from './shared/utilities/app.constant';
import { TimezoneHelper } from './shared/utilities/app.helper';

const MESSAGE_ALERT_CHANGE_ROLE =
  'Your role & permission have been changed. The application needs to be refreshed!';
const MESSAGE_ALERT_USER_DELETED =
  'You currently do not have enough permissions to access the system, please contact admin.';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LayoutComponent, ConfirmDialogComponent, RouterModule],
})
export class AppComponent implements OnInit {
  // Define Default Input of Dialog for this Component
  confirmDialog: IConfirmDialog = AppConstant.DEFAULT_DIALOG;

  // To skip firsttime access app and have internet --> Prevent close dialog
  firstEmitCheckingInternet: boolean = false;
  isCompleteSettingToken: boolean = false;
  private socketSubscribedUserId: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private _msalBroadcastService: MsalBroadcastService,
    private _msalService: MsalService,
    public _azureAdService: AzureAdService,
    private _userInfoService: UserInfoService,
    private offlineDetectionService: OfflineDetectionService,
    private _notificationService: NotificationService,
    private router: Router,
    private _appInsights: ApplicationInsightsService,
    private _io: WebSocketService,
    private _notificationEventService: NotificationEventService,
    private _confirmService: ConfirmDialogService,
    private _locationService: LocationService
  ) {
    this._appInsights = new ApplicationInsightsService(router);
    this.offlineDetectionService
      .getOnlineStatus()
      .subscribe((online: boolean) => {
        if (online) {
          if (!this.firstEmitCheckingInternet) {
            this.firstEmitCheckingInternet = true;
          } else {
            this._confirmService.setDialog({
              isVisible: true,
              header: 'Warning',
              haveDialogMessage: true,
              dialogMessage:
                'You appear to be disconnected from the internet. Please connect to a network to continue using our app.',
              havePrimaryButton: true,
              primaryButtonLabel: 'Reconnecting ... ',
              isValidPrimaryButton: true,
              disablePrimaryButton: true,
              alignCenterButton: true,
              styleDialog: 'warning-dialog',
              buttonEvent: (event: OptionButtonType) =>
                this.onButtonClickDialog(event, 'CheckConnection'),
            });

            this._notificationService.setMessage({
              type: AppConstant.MESSAGE_TYPE.SUCCESS,
              header: 'Network Available',
              content: 'Network connection has been restored.',
            });

            setTimeout(() => {
              this._confirmService.clearDialog();
            }, 3000);
          }
        } else {
          this._confirmService.setDialog({
            isVisible: true,
            header: 'Warning',
            haveDialogMessage: true,
            dialogMessage:
              'You appear to be disconnected from the internet. Please connect to a network to continue using our app.',
            havePrimaryButton: true,
            primaryButtonLabel: 'Reconnect',
            isValidPrimaryButton: true,
            disablePrimaryButton: false,
            alignCenterButton: true,
            styleDialog: 'warning-dialog',
            buttonEvent: (event: OptionButtonType) =>
              this.onButtonClickDialog(event, 'CheckConnection'),
          });
        }
      });
  }

  ngOnInit() {
    TimezoneHelper.getIanaTimezoneListWithUtcOffset();

    const accounts = this._msalService.instance?.getAllAccounts?.() || [];
    if (
      accounts.length > 0 &&
      !this._msalService.instance?.getActiveAccount?.()
    ) {
      this._msalService.instance?.setActiveAccount?.(accounts[0]);
    }

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          [
            EventType.LOGIN_SUCCESS,
            EventType.ACQUIRE_TOKEN_SUCCESS,
            EventType.ACQUIRE_TOKEN_FAILURE,
            EventType.LOGIN_FAILURE,
          ].includes(msg.eventType as any)
        ),
        distinctUntilChanged((prev, curr) => {
          if (
            prev.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
            curr.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          ) {
            const prevToken = (prev.payload as AuthenticationResult)
              .accessToken;
            const currToken = (curr.payload as AuthenticationResult)
              .accessToken;
            return prevToken === currToken;
          }
          return false;
        }),
        tap((msg: EventMessage) => {
          const { accessToken, account } = msg.payload as AuthenticationResult;
          const currentActive = this._msalService.instance.getActiveAccount();

          switch (msg.eventType) {
            case EventType.LOGIN_SUCCESS:
              if (!currentActive && account)
                this._msalService.instance.setActiveAccount(account);
              break;
            case EventType.ACQUIRE_TOKEN_SUCCESS:
              if (currentActive && !this.isCompleteSettingToken) {
                this.isCompleteSettingToken = true;
                this._io.connectSocket(accessToken);
                this.getUserData(currentActive.localAccountId);
                localStorage.setItem(
                  'loginUserId',
                  currentActive.localAccountId
                );
              }
              localStorage.setItem('token', accessToken);
              break;
            case EventType.ACQUIRE_TOKEN_FAILURE:
            case EventType.LOGIN_FAILURE:
              this._confirmService.setDialog({
                isVisible: true,
                header: 'Information',
                haveDialogMessage: true,
                dialogMessage: 'Session timeout. Please login again!',
                havePrimaryButton: true,
                primaryButtonLabel: 'Login',
                isValidPrimaryButton: true,
                disablePrimaryButton: false,
                buttonEvent: (event: OptionButtonType) =>
                  this.onButtonClickDialog(event, 'Login'),
              });
              break;
            default:
              break;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {});

    this._confirmService.dataSubjectAction$.subscribe(
      (data: IConfirmDialog) => {
        this.confirmDialog = data;
      }
    );

    this.router.events
      .pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd))
      .subscribe(event => {
        const activatedComponent = this.getActivatedComponent(event.state.root);
        if (activatedComponent) {
          this._appInsights.trackPageView({
            name: activatedComponent.name,
            uri: event.urlAfterRedirects,
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.unlistenWebSocketEvents();
    localStorage.removeItem('token');
    localStorage.removeItem('loginUserId');
    // Complete all subjects for cleanup
    this.destroy$.next();
    this.destroy$.complete();
  }

  listenWebSocketEvents(userObject: any) {
    const userId = String(userObject?.id || '');
    if (!userId) return;

    // Ensure listener registration is idempotent across repeated auth events.
    if (
      this.socketSubscribedUserId &&
      this.socketSubscribedUserId !== userId
    ) {
      this.unlistenWebSocketEvents(this.socketSubscribedUserId);
    }
    this.unlistenWebSocketEvents(userId);

    const rolePermission = [
      AppConstant.ROLES.ADMIN.value.data,
      AppConstant.ROLES.SUPPORT.value.data,
    ];
    if (rolePermission.includes(userObject.role)) {
      this._io
        .listen('live-support-admin')
        .subscribe((event: any) => this._io.handleMessage(event));
    }

    this._io
      .listen(`message/${userId}`)
      .subscribe((event: any) => this._io.handleMessage(event));

    this._io.listen('notification_events/all/new').subscribe((event: any) => {
      this._notificationEventService.decreaseUnreadCount(-1, event);
    });

    this._io
      .listen(`notification_events/${userId}/new`)
      .subscribe((event: any) => {
        this._notificationEventService.decreaseUnreadCount(-1, event);
      });

    this._io
      .listen(`update-user-info-${userId}`)
      .subscribe((res: any) => {
        if (res?.isDelete) {
          this._confirmService.setDialog({
            isVisible: true,
            header: 'Information',
            haveDialogMessage: true,
            dialogMessage: MESSAGE_ALERT_USER_DELETED,
            havePrimaryButton: true,
            primaryButtonLabel: 'Login',
            isValidPrimaryButton: true,
            disablePrimaryButton: false,
            disableSecondaryButton: true,
            buttonEvent: (event: OptionButtonType) =>
              this.onButtonClickDialog(event, 'UserRemove'),
          });
        }
        if (res.isChangeUserRole) {
          this._confirmService.setDialog({
            isVisible: true,
            header: 'Information',
            haveDialogMessage: true,
            dialogMessage: MESSAGE_ALERT_CHANGE_ROLE,
            havePrimaryButton: true,
            primaryButtonLabel: 'Yes',
            isValidPrimaryButton: true,
            disablePrimaryButton: false,
            disableSecondaryButton: true,
            buttonEvent: (event: OptionButtonType) =>
              this.onButtonClickDialog(event, 'ChangeRole'),
          });
        }
        this._userInfoService.setUserInfor(res.data);
      });

    this.socketSubscribedUserId = userId;
  }

  unlistenWebSocketEvents(userId?: string | number) {
    const fallbackUserId = this._userInfoService.userSubject.getValue()?.id;
    const resolvedUserId = String(
      userId ?? this.socketSubscribedUserId ?? fallbackUserId ?? ''
    );

    if (!resolvedUserId) return;

    this._io.unlisten(`update-user-info-${resolvedUserId}`);
    this._io.unlisten(`notification_events/${resolvedUserId}/new`);
    this._io.unlisten(`message/${resolvedUserId}`);
    this._io.unlisten('notification_events/all/new');
    this._io.unlisten('live-support-admin');

    if (this.socketSubscribedUserId === resolvedUserId) {
      this.socketSubscribedUserId = null;
    }
  }

  getUserData(loginID: string, maxRetries: number = 3) {
    // Preload country/region reference data now that the user is authenticated.
    this._locationService.preload();

    // Apply retry to each API call
    this._userInfoService
      .getUserInfoInitiate(loginID)
      .pipe(retry(maxRetries))
      .subscribe({
        next: (user: User) => {
          this.listenWebSocketEvents(user);
          this._userInfoService.setUserInfor(user);
        },
        error: error => {
          if (!error || error?.includes('authenticate failed')) return;

          setTimeout(() => {
            this._confirmService.initDataErrorDialog(error);
          }, 1000);
        },
      });
  }

  private getActivatedComponent<T>(snapshot: ActivatedRouteSnapshot): Type<T> {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }
    return snapshot.component as Type<T>;
  }

  onButtonClickDialog(
    option: OptionButtonType,
    dialogType: DialogActionApp
  ): void {
    switch (option) {
      case AppConstant.OPTION_BUTTON.YES:
        switch (dialogType) {
          case 'Reload':
            window.location.reload();
            break;
          case 'CheckConnection':
            this.requestCheckingConnections();
            break;
          case 'ChangeRole':
            this._confirmService.clearDialog();
            window.location.reload();
            break;
          case 'UserRemove':
            this.logout_Azure();
            break;
          default:
            break;
        }
        break;
      case AppConstant.OPTION_BUTTON.CANCEL:
        this._confirmService.clearDialog();
        break;
      default:
        break;
    }
  }

  requestCheckingConnections() {
    this._confirmService.setDialog({
      havePrimaryButton: true,
      primaryButtonLabel: 'Reconnecting ... ',
      isValidPrimaryButton: true,
      disablePrimaryButton: true,
    });

    setTimeout(() => {
      this.offlineDetectionService.getStableConnect().subscribe({
        next: () => {
          // duplicate
          // Dont need show message
        },
        error: () => {
          this._confirmService.setDialog({
            havePrimaryButton: true,
            primaryButtonLabel: 'Reconnect',
            isValidPrimaryButton: true,
            disablePrimaryButton: false,
          });

          this._notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.ERROR,
            header: 'Reconnect',
            content:
              'Looks like you are offline; please reconnect and try again.',
          });
        },
      });
    }, 3000);
  }

  logout_Azure() {
    const currentAccount = this._msalService.instance.getActiveAccount();

    this._azureAdService.isUserLoggedIn.next(false);
    this._azureAdService.userNameLoggedIn.next(undefined);
    this._msalService.logoutRedirect({
      account: currentAccount,
      postLogoutRedirectUri: environment.logoutUrl,
    });
  }
}
